import type { GettextInterface } from '../../../../../../../../shared/I18n';
import type { QualificationAttribute } from '../../../types/JobAttribute';
import type { TileSectionSentimentCounts } from '../../common/TileSection';

import {
    getQualificationTrackingId,
    VALID_YELLOWLIGHT_CATEGORIES,
} from './useQualificationSections';
import {
    Certification,
    Discover,
    JobTag,
    Language,
    Publication,
} from '../../../../../../../../mads';
import Sentiment from '../../../../../../../../shared/util/salary/Sentiment';
import ViewType from '../../../constants/ViewType';
import JobTagStrictness from '../../../requirements/JobTagStrictness';
import YellowLightCategoryType from '../../../types/YellowLightCategoryType';

import { colors } from '@indeed/ifl-tokens';

export function filterByCategory(
    attributes: QualificationAttribute[],
    type: YellowLightCategoryType,
): QualificationAttribute[] {
    return attributes.filter(Boolean).filter((att) => type === att.type);
}

/**
 * Compares two attribute arrays and returns SUIDs of attributes where sentiment differs.
 * Useful for debugging or tracking which attributes have changed sentiment between
 * the original props and local state.
 *
 * @param originalAttributes - The original attributes array (e.g., from props)
 * @param updatedAttributes - The updated attributes array (e.g., from local state)
 * @returns Array of SUIDs for attributes with sentiment discrepancies
 */
export function getAttributesWithSentimentDiscrepancies(
    originalAttributes: QualificationAttribute[],
    updatedAttributes: QualificationAttribute[],
): string[] {
    const discrepancies: string[] = [];

    // Create a map of suid -> sentiment for quick lookup
    const updatedMap = new Map(updatedAttributes.map((attr) => [attr.suid, attr.sentiment]));

    originalAttributes.forEach((originalAttr) => {
        const updatedSentiment = updatedMap.get(originalAttr.suid);

        // If the attribute exists in both arrays and sentiment differs
        if (updatedSentiment !== undefined && updatedSentiment !== originalAttr.sentiment) {
            discrepancies.push(originalAttr.suid);
        }
    });

    return discrepancies;
}

export function getUnknownAttributes(
    attributes: QualificationAttribute[],
    answeredIds: string[],
): QualificationAttribute[] {
    return attributes.filter(
        (att) =>
            att?.sentiment === Sentiment.UNKNOWN && !answeredIds.includes(att.uuid || att.suid),
    );
}

export function getYellowLightTileList(
    attributes: QualificationAttribute[],
    showCount: number | undefined,
    answeredIds: string[] = [],
): QualificationAttribute[] {
    if (!showCount) {
        return attributes;
    }

    const unknown: QualificationAttribute[] = getUnknownAttributes(attributes, answeredIds);
    const sliced = attributes.slice(0, showCount);

    // If there are unknown requirements and none are visible, swap with last visible
    if (unknown.length && !unknown.some((req) => sliced.includes(req))) {
        const lastVisibleIndex = showCount - 1;
        const firstUnknownIndex = attributes.findIndex((el) => el === unknown[0]);

        [attributes[lastVisibleIndex], attributes[firstUnknownIndex]] = [
            attributes[firstUnknownIndex],
            attributes[lastVisibleIndex],
        ];
    }

    return attributes;
}

export function getRequirementsTitleText(
    type: YellowLightCategoryType,
    gt: GettextInterface,
): string {
    switch (type) {
        case YellowLightCategoryType.CERTIFICATIONS:
            return gt.gettext('Certifications');
        case YellowLightCategoryType.EDUCATION:
            return gt.gettext('Education');
        case YellowLightCategoryType.LICENSE:
            return gt.gettext('Licenses');
        case YellowLightCategoryType.LANGUAGES:
            return gt.gettext('Languages');
        case YellowLightCategoryType.SKILLS:
        // fall through
        default:
            return gt.gettext('Skills');
    }
}

export const getYellowLightSectionIcon = (type: YellowLightCategoryType) => {
    switch (type) {
        case YellowLightCategoryType.SKILLS:
            return Discover;
        case YellowLightCategoryType.CERTIFICATIONS:
            return Certification;
        case YellowLightCategoryType.EDUCATION:
            return Publication;
        case YellowLightCategoryType.LANGUAGES:
            return Language;
        case YellowLightCategoryType.LICENSE:
        // fall through
        default:
            return JobTag;
    }
};

export const getMissingRequirement = (
    attributes: QualificationAttribute[],
    type: YellowLightCategoryType,
    answeredIds: string[],
): QualificationAttribute | undefined => {
    return attributes.find((req) => isMissingRequirement(req, type, answeredIds));
};

export const getAllMissingQualifications = (
    attributes: QualificationAttribute[],
    answeredIds: string[],
): Array<QualificationAttribute> => {
    return attributes.filter((req) => isMissingRequirement(req, req.type, answeredIds));
};

const isMissingRequirement = (
    qualification: QualificationAttribute,
    type: YellowLightCategoryType,
    answeredIds: string[],
): boolean => {
    const isValidType = VALID_YELLOWLIGHT_CATEGORIES.includes(qualification.type);
    const isUnsortedReq = type === YellowLightCategoryType.UNSORTED;
    const isUnmetReq = qualification.sentiment !== Sentiment.POSITIVE;
    const isReqAnswered = answeredIds.includes(getQualificationTrackingId(qualification));

    return (
        ((isUnsortedReq && isValidType) || type === qualification.type) &&
        isUnmetReq &&
        !isReqAnswered &&
        !(qualification.sentiment === Sentiment.NEGATIVE)
    );
};

export const getTileSectionSentimentCount = (
    attributes: QualificationAttribute[],
): TileSectionSentimentCounts => {
    const sentiments: TileSectionSentimentCounts = {
        total: 0,
        positive: 0,
        negative: 0,
        unknown: 0,
    };
    attributes.forEach((req) => {
        sentiments.total += 1;
        switch (req.sentiment) {
            case Sentiment.POSITIVE:
                sentiments.positive += 1;
                break;
            case Sentiment.NEGATIVE:
                sentiments.negative += 1;
                break;
            case Sentiment.UNKNOWN:
            default:
                sentiments.unknown += 1;
                break;
        }
    });
    return sentiments;
};

const getRequirementSortMapping = (req: QualificationAttribute, answeredIds: string[]) => {
    const justAnswered = answeredIds.includes(req.uuid);
    const isRequired = req.strictness === JobTagStrictness.REQUIRED;
    const isTypicallyRequired = req.strictness === JobTagStrictness.TYPICALLY_REQUIRED;
    const isPreferred = req.strictness === JobTagStrictness.PREFERRED;

    const isReqMet = req.sentiment === Sentiment.POSITIVE;
    const isNegMatch = req.sentiment === Sentiment.NEGATIVE;
    const isUnknown = !isReqMet && !isNegMatch && !justAnswered;

    const sortMap = [
        isRequired && justAnswered,
        isRequired && isUnknown,
        isRequired && isReqMet,
        isTypicallyRequired && justAnswered,
        isTypicallyRequired && isUnknown,
        isTypicallyRequired && isReqMet,
        isPreferred && justAnswered,
        isPreferred && isUnknown,
        isPreferred && isReqMet,
        justAnswered,
        isUnknown,
        isReqMet,
        isNegMatch,
    ];

    return sortMap.includes(true) ? sortMap.indexOf(true) : sortMap.length;
};

const getRYGRequirementSortMapping = (req: QualificationAttribute, viewType: ViewType) => {
    const isRequired = req.strictness === JobTagStrictness.REQUIRED;
    const isTypicallyRequired = req.strictness === JobTagStrictness.TYPICALLY_REQUIRED;

    const isReqMet = req.sentiment === Sentiment.POSITIVE;
    const isNegMatch = req.sentiment === Sentiment.NEGATIVE;
    const isUnknown = !isReqMet && !isNegMatch;

    const sortMapRed = [
        isNegMatch && isRequired,
        isUnknown && isRequired,
        isNegMatch && isTypicallyRequired,
        isUnknown && isTypicallyRequired,
        isNegMatch,
        isUnknown,
        isReqMet && isRequired,
        isReqMet && isTypicallyRequired,
        isReqMet,
    ];

    const sortMapYellow = [
        isUnknown && isRequired,
        isReqMet && isRequired,
        isNegMatch && isRequired,
        isUnknown && isTypicallyRequired,
        isReqMet && isTypicallyRequired,
        isNegMatch && isTypicallyRequired,
        isUnknown,
        isReqMet,
        isNegMatch,
    ];

    const sortMapGreen = [
        isReqMet && isRequired,
        isUnknown && isRequired,
        isReqMet && isTypicallyRequired,
        isUnknown && isTypicallyRequired,
        isReqMet,
        isUnknown,
        isNegMatch && isRequired,
        isNegMatch && isTypicallyRequired,
        isNegMatch,
    ];

    let sortMap;

    switch (viewType) {
        case ViewType.RED:
            sortMap = sortMapRed;
            break;
        case ViewType.GREEN:
            sortMap = sortMapGreen;
            break;
        default:
            sortMap = sortMapYellow;
    }

    return sortMap.includes(true) ? sortMap.indexOf(true) : sortMap.length;
};
export const sortRequirements = (
    attributes: QualificationAttribute[],
    category: YellowLightCategoryType,
    answeredIds: string[],
): QualificationAttribute[] => {
    const categoryReqs = filterByCategory(attributes, category);

    return categoryReqs.sort((a, b) => {
        const sortMappingA = getRequirementSortMapping(a, answeredIds);
        const sortMappingB = getRequirementSortMapping(b, answeredIds);

        if (sortMappingB !== sortMappingA) {
            return sortMappingA - sortMappingB;
        }

        if (a.label < b.label) {
            return 1;
        } else if (a.label > b.label) {
            return -1;
        } else {
            return 0;
        }
    });
};

export const sortRequirementsByTrafficLights = (
    attributes: QualificationAttribute[],
    viewType: ViewType,
): QualificationAttribute[] => {
    const filteredValidAttributes = attributes
        .filter(Boolean)
        .filter((att) => VALID_YELLOWLIGHT_CATEGORIES.includes(att.type));

    return filteredValidAttributes.sort((a, b) => {
        const sortMappingA = getRYGRequirementSortMapping(a, viewType);
        const sortMappingB = getRYGRequirementSortMapping(b, viewType);

        if (sortMappingB !== sortMappingA) {
            return sortMappingA - sortMappingB;
        }

        if (a.label < b.label) {
            return 1;
        } else if (a.label > b.label) {
            return -1;
        } else {
            return 0;
        }
    });
};
export function getSubtitleProps(gt: GettextInterface, viewType: ViewType) {
    const defaultLabelText = gt.gettext(
        'Here’s how the job qualifications align with your [profile|{0}].',
        ['link'],
    );

    switch (viewType) {
        case ViewType.RED:
            return {
                sx: { color: colors.warning[700], fontWeight: 'bold' },
                label: gt.gettext(
                    'Your [profile|{0}] might be [missing|{1}] qualifications mentioned in the job description',
                    ['link', 'highlight'],
                ),
            };
        case ViewType.GREEN:
            return {
                sx: { color: colors.success[800], fontWeight: 'bold' },
                label: gt.gettext(
                    'You have [matching|{0}] qualifications, based on your [profile|{1}] and the job description',
                    ['highlight', 'link'],
                ),
            };
        default:
            return {
                label: defaultLabelText,
            };
    }
}
